import { Link } from "react-router-dom";
import {
  Building2, Landmark, Store, Droplets, PaintBucket, Leaf,
  Hammer, Building, KeyRound, DoorOpen, Phone,
} from "lucide-react";
import { SITE, waLink } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow, RoadDash } from "@/components/bits";
import { QuoteCallout } from "./Home";

const HERO_IMG = "/work/property-care.jpg";

const CORE = [
  { icon: Building2, title: "Property Maintenance", copy: "Regular upkeep that keeps residential and commercial properties safe, functional and looking sharp." },
  { icon: Landmark, title: "Public Sector Maintenance", copy: "Dependable maintenance work for government and public-sector sites, done to spec and on schedule." },
  { icon: Store, title: "Commercial Maintenance", copy: "Ongoing maintenance for businesses, offices and commercial properties across the island." },
];

const ALSO = [
  { icon: Droplets, title: "Pressure Washing", copy: "Driveways, walls, patios and surfaces washed clean of grime and mildew." },
  { icon: PaintBucket, title: "Painting and Touch-ups", copy: "Fresh paint and careful touch-ups inside and out." },
  { icon: Leaf, title: "Yard and Outdoor Maintenance", copy: "Overgrowth cleared, yards tidied and outdoor spaces kept presentable." },
  { icon: Hammer, title: "Minor Repairs", copy: "Those small jobs that stack up — handled quickly and properly." },
  { icon: Building, title: "Exterior Maintenance", copy: "Walls, fences, gutters and outdoor features kept in good order." },
  { icon: KeyRound, title: "Property Preparation", copy: "Getting a property ready for sale, rental or a change of season." },
  { icon: DoorOpen, title: "Rental Property Maintenance", copy: "Dependable upkeep for landlords and rental properties across the island." },
];

export default function Maintenance() {
  usePageTitle("Maintenance");
  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/10" data-testid="maintenance-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-joell-black/75 via-joell-black/60 to-joell-black" aria-hidden="true" />
        <div className="container-x relative pb-20 pt-36 md:pb-28 md:pt-48">
          <Reveal>
            <Eyebrow>More than asphalt</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl">
              Property &amp; maintenance services
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              JOELL is more than asphalt. We provide practical maintenance solutions that help Bermuda homeowners,
              landlords, businesses and the public sector keep their properties safe, functional and well maintained.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/request-quote" className="btn-accent !px-8 !py-4" data-testid="maintenance-quote-button">Request a Free Quote</Link>
              <a href={SITE.phoneHref} className="btn-outline !px-8 !py-4" data-testid="maintenance-call-button"><Phone size={15} /> Call {SITE.phone}</a>
            </div>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0"><RoadDash /></div>
      </section>

      <section className="container-x py-20 md:py-28" data-testid="maintenance-services-section">
        <SectionHead
          eyebrow="What we do"
          title="Practical help, all around your property"
          sub="If it needs fixing, cleaning, painting or maintaining — talk to JOELL."
        />
        <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
          {CORE.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <div className="group h-full bg-joell-black p-8 transition-colors duration-300 hover:bg-joell-surface md:p-10" data-testid={`maintenance-core-card-${i}`}>
                <div className="flex items-start justify-between">
                  <s.icon size={30} strokeWidth={1.6} className="text-joell-yellow" />
                  <span className="font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.08}>
          <h3 className="mt-16 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500">Also available</h3>
        </Reveal>
        <div className="mt-6 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {ALSO.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.03}>
              <div className="group h-full bg-joell-black p-6 transition-colors duration-300 hover:bg-joell-surface" data-testid={`maintenance-service-card-${i}`}>
                <s.icon size={22} strokeWidth={1.6} className="text-joell-yellow" />
                <h4 className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-white">{s.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-start justify-between gap-6 border border-white/10 bg-joell-coal/60 p-8 sm:p-10 md:flex-row md:items-center" data-testid="maintenance-ask-joell">
            <p className="max-w-xl font-display text-xl font-semibold uppercase leading-snug text-white sm:text-2xl">
              Don't see your job listed? Contact us and tell us what you need.
            </p>
            <a
              href={waLink("Hi JOELL, I have a job that needs doing and I don't see it listed on your website.")}
              target="_blank"
              rel="noreferrer"
              className="btn-accent !px-8 !py-4"
              data-testid="maintenance-ask-joell-button"
            >
              Ask JOELL
            </a>
          </div>
        </Reveal>
      </section>

      <QuoteCallout />
    </>
  );
}
